import Client from './Client'

export interface IToken {
  // uid: string
  accessToken: string
  tokenType: string
  idToken: string
  expiresIn: number
  refreshToken: string
}

export interface IUserInfo {
  uid: string
  email: string
  emailVerified: boolean
  firstName?: string
  middleName?: string
  familyName?: string
  name?: string
  gender?: string
  birthdate?: string
  zoneinfo?: string
  locale?: string
  website?: string
  createdAt?: string
  updatedAt?: string
  loggedAt?: string
  picture?: string
  profile?: {}
}

export interface ISession {
  sessionId: string
}

export interface IEmail {
  email: string
}

export interface IProvider {
  uid: string
  name: string
  email: string
  firstName: string
  middleName: string
  familyName: string
  picture: string
  rawUserInfo: {}
}

export interface IProviders {
  providers: IProvider[]
  // registered: boolean;
}

export interface ICredentialsProvider {
  provider: string // todo: enum providers
  accessToken?: string
  idToken?: string
  oauthTokenSecret?: string
}

export interface ICredentialsIdToken {
  idToken: string
}

export interface ICredentialsCode {
  code: string
}

/**
 * Класс для работы с апи v1.0.1
 */
export default class Auth {
  constructor(public readonly client: Client) {}

  // Регистрация пользователя
  // -
  // Коды ошибок:
  // email_exists
  // operation_not_allowed - данная операция отключена
  // invalid_fields
  // captcha_required
  // invalid_captcha
  // too_many_attempts?
  signup(payload: {
    email: string
    password: string
    captcha?: string
  }): Promise<IToken> {
    return this.client.app
      .createRequest('/auth/signup')
      .postJson(payload)
      .execute()
  }

  // description
  // -
  // Коды ошибок:
  // temp(payload: {}): Promise<any> {
  //   return this._client.post('/temp', payload);
  // }

  // Создает новую либо возвращает текущую сессию (переданную через хедер запроса)
  // -
  // Коды ошибок:
  // operation_not_allowed - данная операция отключена
  // too_many_attempts?
  obtainSession(): Promise<ISession> {
    return this.client.app
      .createRequest('/auth/obtainSession')
      .postJson({})
      .execute()
  }

  // Captcha
  // -
  // Коды ошибок:
  // operation_not_allowed - данная операция отключена
  // invalid_session - сессия не найдена либо не переданна
  // too_many_attempts?
  captcha(params: { sessionId?: string; refresh?: boolean }): Promise<Blob> {
    return this.client.app
      .createRequest('/auth/captcha')
      .setResponseType('blob')
      .setQueryParams(params)
      .execute()
    // .then((response) => {
    //   const arrayBufferView = new Uint8Array(
    //     (response.result as XMLHttpRequest).response
    //   )
    //   const blob = new Blob([arrayBufferView], {
    //     type:
    //       (response.result as XMLHttpRequest).getResponseHeader(
    //         'Content-Type'
    //       ) || undefined,
    //   })
    //   const urlCreator = window.URL || (window as any).webkitURL
    //   response.result = urlCreator.createObjectURL(blob)
    //
    //   return response
    // })
  }

  createCaptchaUrl(params: { sessionId?: string; refresh?: boolean }): string {
    return this.client.app.createUrl('/auth/captcha', params)
  }

  // Авторизация пользователя по логину и емейлу, создание и возврат ID (JWT) и refresh токенов

  // -
  // Коды ошибок:
  // email_not_found
  // invalid_password
  // user_blocked
  // captcha_required
  // invalid_captcha
  // too_many_attempts?
  loginWithEmailAndPassword(payload: {
    email: string
    password: string
    captcha?: string
  }): Promise<IToken> {
    return this.client.app
      .createRequest('/auth/loginWithEmailAndPassword')
      .postJson(payload)
      .execute()
  }

  // Авторизация пользователя по access токену и его провайдеру, создание и возврат ID (JWT),
  // access и refresh токенов Если провайдер scorocode - в токене должны быть перечислены
  // дополнительные права пользователя на доступ к внутренним сервисам приложения
  // (postgre, mongo, functions и тд.)
  // -
  // Коды ошибок:
  // unlinked_identity
  // provider_not_allowed
  // invalid_credentials
  // too_many_attempts
  loginWithCredentials(payload: {
    credentials: ICredentialsProvider
  }): Promise<
    IToken & {
      provider: string
      provider_info: IProvider
      provider_credentials: ICredentialsProvider
    }
  > {
    return this.client.app
      .createRequest('/auth/loginWithCredentials')
      .postJson(payload)
      .execute()
  }

  // Logout user
  logout(): Promise<void> {
    return this.client.app.createRequest('/auth/logout').execute()
  }

  // Получить данные пользователя
  // -
  // Коды ошибок:
  // operation_not_allowed - данная операция отключена
  // invalid_id_token - неверные либо стухший id token аккаунта приложения
  // account_not_found - аккаунт не найден - пользователь был удален
  getUser(payload: ICredentialsIdToken): Promise<IUserInfo> {
    return this.client.app
      .createRequest('/auth/getUser')
      .postJson(payload)
      .execute()
  }

  // Список привязанных к пользователю oauth2 аккаунтов
  // -
  // Коды ошибок:
  // invalid_id_token - неверный либо стухший id token аккаунта приложения
  getLinkedProviders(payload: ICredentialsIdToken): Promise<IProviders> {
    return this.client.app
      .createRequest('/auth/getLinkedProviders')
      .postJson(payload)
      .execute()
  }

  // Привязать к пользователю внешний oauth2 аккаунт по его ключам доступа
  // -
  // Коды ошибок:
  // provider_not_allowed - провайдер отключен unknown_provider - провайдер не поддерживается
  // invalid_idp_response - неверный ответ от провайдера (неверные либо протухшие токены)
  // invalid_id_token - неверный либо стухший id token аккаунта приложения
  // user_is_already_linked - привязываемый аккаунт уже привязан к другой учетке
  // provider_is_already_linked - к аккаунту уже привязан провайдер

  linkProvider(payload: {
    idToken: string
    // provider: string;
    credentials: ICredentialsProvider
  }): Promise<IProviders> {
    return this.client.app
      .createRequest('/auth/linkProvider')
      .postJson(payload)
      .execute()
  }

  // Список привязанных к пользователю oauth2 аккаунтов
  // -
  // Коды ошибок:
  // invalid_id_token - неверный либо стухший id token аккаунта приложения

  unlinkProvider(payload: {
    idToken: string
    provider: string
  }): Promise<IProviders> {
    return this.client.app
      .createRequest('/auth/unlinkProvider')
      .postJson(payload)
      .execute()
  }

  // Обновить ID и access токены с помощью refreshToken
  // -
  // Коды ошибок:
  // token_expired - токен протух
  // account_disabled - пользователь заблокирован
  // account_not_found - пользователь не найден
  // invalid_refresh_token - токен не найден либо пуст
  refreshToken(payload: { refreshToken: string }): Promise<IToken> {
    return this.client.app
      .createRequest('/auth/refreshToken')
      .postJson(payload)
      .execute()
  }

  // Обновить данные пользователя
  // -
  // Коды ошибок:
  // invalid_id_token - неверные либо стухший id token аккаунта приложения
  updateUser(payload: {
    idToken: string
    user: IUserInfo
  }): Promise<
    IToken & {
      info: IUserInfo
    }
  > {
    return this.client.app
      .createRequest('/auth/updateUser')
      .postJson(payload)
      .execute()
  }

  // Удалить пользователя
  // -
  // Коды ошибок:
  // invalid_id_token - неверные либо стухший id token аккаунта приложения
  deleteUser(payload: { idToken: string }): Promise<void> {
    return this.client.app
      .createRequest('/auth/deleteUser')
      .postJson(payload)
      .execute()
  }

  // Изменить пароль по ID токену
  // -
  // Коды ошибок:
  // invalid_id_token - неверные либо стухший id token аккаунта приложения
  // weak_password - слабый пароль
  changePassword(payload: {
    idToken: string
    password: string
  }): Promise<IToken> {
    return this.client.app
      .createRequest('/auth/changePassword')
      .postJson(payload)
      .execute()
  }

  // Изменить пароль по коду высланному на email
  // -
  // Коды ошибок:
  // operation_not_allowed - данная операция отключена
  // weak_password - слабый пароль
  // user_disabled - пользователь заблокирован
  // expired_code - протухший код
  // invalid_code - не известный/использованный код
  changePasswordByCode(payload: {
    code: string
    password: string
  }): Promise<IEmail> {
    return this.client.app
      .createRequest('/auth/changePasswordByCode')
      .postJson(payload)
      .execute()
  }

  // Отправить код для изменения пароля на емеил пользователя
  // -
  // Коды ошибок:
  // operation_not_allowed - данная операция отключена
  // invalid_email - почта не верна
  // email_not_found - аккаунт с данным емеилом не найден
  sendResetPasswordEmail(payload: { email: string }): Promise<IEmail> {
    return this.client.app
      .createRequest('/auth/sendResetPasswordEmail')
      .postJson(payload)
      .execute()
  }

  // Отправить код для подтверждения email пользователя
  // -
  // Коды ошибок:
  // operation_not_allowed - данная операция отключена
  // invalid_id_token - неверные либо стухший id token аккаунта приложения
  // account_not_found - аккаунт не найден - пользователь был удален
  sendAccountConfirmationEmail(payload: ICredentialsIdToken): Promise<IEmail> {
    return this.client.app
      .createRequest('/auth/sendAccountConfirmationEmail')
      .postJson(payload)
      .execute()
  }

  // Подтвердить емеил аккаунта по коду высланному на email
  // -
  // Коды ошибок:
  // operation_not_allowed - данная операция отключена
  // expired_code - протухший код
  // invalid_code - не известный/использованный код
  // account_not_found - аккаунт не найден - пользователь был удален
  confirmEmail(payload: ICredentialsCode): Promise<IEmail> {
    return this.client.app
      .createRequest('/auth/confirmEmail')
      .postJson(payload)
      .execute()
  }
}
